import { c as _c } from "react/compiler-runtime";
import Image, { type ImageProps } from 'next/image';
import { cn } from '@/lib/utils';

// Single source of truth for tech-stack logos.
//
// Consumed by ProjectCard (projects index) and FeaturedProjects (home). Add a logo here once — never per
// component — so variants can't drift out of sync between surfaces.
//
// A logo is either a single theme-neutral asset, or a { dark, light } pair.
// Pairs are REQUIRED whenever the only available art is a white (dark-mode)
// mark, otherwise it disappears against the light pill background.
import logoAppleDark from '@/images/library/Apple_dark.svg';
import logoAppleLight from '@/images/library/Apple_light.svg';
import logoChrome from '@/images/library/chrome.svg';
import logoClerkDark from '@/images/library/clerk-dark.svg';
import logoClerkLight from '@/images/library/clerk-light.svg';
import logoDocker from '@/images/library/docker.svg';
import logoDrizzle from '@/images/library/drizzle-orm_dark.svg';
import logoExpoDark from '@/images/library/expo-dark.svg';
import logoExpoLight from '@/images/library/expo-light.svg';
import logoFastAPI from '@/images/library/fastapi.svg';
import logoGemini from '@/images/library/gemini_wordmark.svg';
import logoHume from '@/images/library/hume-ai.svg';
import logoLinear from '@/images/library/linear.svg';
import logoNextjs from '@/images/library/nextjs_icon_dark.svg';
import logoOpenAIDark from '@/images/library/OpenAI_dark.svg';
import logoOpenAILight from '@/images/library/OpenAI_light.svg';
import logoPinecone from '@/images/library/pinecone.svg';
import logoPlanetScaleDark from '@/images/library/PlanetScale_dark.svg';
import logoPlanetScaleLight from '@/images/library/PlanetScale_light.svg';
import logoPostgres from '@/images/library/postgresql.svg';
import logoPX4 from '@/images/library/px4_logo.svg';
import logoPython from '@/images/library/python.svg';
import logoPytorch from '@/images/library/pytorch.svg';
import logoRadixDark from '@/images/library/radixui_dark.svg';
import logoRadixLight from '@/images/library/radixui_light.svg';
import logoReact from '@/images/library/react.svg';
import logoROS2 from '@/images/library/ros_logo.svg';
import logoSentry from '@/images/library/sentry.svg';
import logoShadcnDark from '@/images/library/shadcn_dark.svg';
import logoShadcnLight from '@/images/library/shadcn_light.svg';
import logoSupabase from '@/images/library/supabase.svg';
import logoTailwind from '@/images/library/tailwindcss.svg';
import logoTRPC from '@/images/library/trpc.svg';
import logoTypeScript from '@/images/library/typescript.svg';
import logoVercelDark from '@/images/library/Vercel_dark.svg';
import logoVercelLight from '@/images/library/Vercel_light.svg';
import logoZustand from '@/images/library/zustand.webp';
type LogoAsset = ImageProps['src'];
type ThemedLogo = {
  dark: LogoAsset;
  light: LogoAsset;
};
type TechLogo = LogoAsset | ThemedLogo;

// Keys cover both display names (e.g. "Next.js", used by the project detail
// `technologies` strings) and short logoKeys (e.g. "Nextjs", used by the
// projects index). Aliases point at the same asset so callers can use either.
const TECH_LOGOS: Record<string, TechLogo> = {
  React: logoReact,
  Nextjs: logoNextjs,
  'Next.js': logoNextjs,
  Tailwind: logoTailwind,
  'Tailwind CSS': logoTailwind,
  OpenAI: {
    dark: logoOpenAIDark,
    light: logoOpenAILight
  },
  'OpenAI API': {
    dark: logoOpenAIDark,
    light: logoOpenAILight
  },
  Hume: logoHume,
  'Hume AI': logoHume,
  Postgres: logoPostgres,
  PostgreSQL: logoPostgres,
  Drizzle: logoDrizzle,
  'Drizzle ORM': logoDrizzle,
  Expo: {
    dark: logoExpoDark,
    light: logoExpoLight
  },
  Supabase: logoSupabase,
  Clerk: {
    dark: logoClerkDark,
    light: logoClerkLight
  },
  'Clerk Auth': {
    dark: logoClerkDark,
    light: logoClerkLight
  },
  TypeScript: logoTypeScript,
  TS: logoTypeScript,
  Python: logoPython,
  FastAPI: logoFastAPI,
  Gemini: logoGemini,
  'Gemini API': logoGemini,
  Pinecone: logoPinecone,
  Vercel: {
    dark: logoVercelDark,
    light: logoVercelLight
  },
  Docker: logoDocker,
  Chrome: logoChrome,
  'Chrome Extension': logoChrome,
  TRPC: logoTRPC,
  tRPC: logoTRPC,
  PlanetScale: {
    dark: logoPlanetScaleDark,
    light: logoPlanetScaleLight
  },
  Shadcn: {
    dark: logoShadcnDark,
    light: logoShadcnLight
  },
  'Shadcn UI': {
    dark: logoShadcnDark,
    light: logoShadcnLight
  },
  Pytorch: logoPytorch,
  PyTorch: logoPytorch,
  Linear: logoLinear,
  Sentry: logoSentry,
  Radix: {
    dark: logoRadixDark,
    light: logoRadixLight
  },
  'Radix UI': {
    dark: logoRadixDark,
    light: logoRadixLight
  },
  Zustand: logoZustand,
  Apple: {
    dark: logoAppleDark,
    light: logoAppleLight
  },
  ROS2: logoROS2,
  PX4: logoPX4
};
function isThemedLogo(logo: TechLogo): logo is ThemedLogo {
  return typeof logo === 'object' && logo !== null && 'dark' in logo;
}
function containsText(value: string, query: string) {
  return value.includes(query);
}

// Resolve a tech name or logoKey to its logo. Tries an exact match first,
// then a substring match so close variants ("OpenAI" vs "OpenAI API") resolve.
function getTechLogo(nameOrKey: string): TechLogo | null {
  if (TECH_LOGOS[nameOrKey]) return TECH_LOGOS[nameOrKey];
  const partial = Object.entries(TECH_LOGOS).find(([key]) => containsText(nameOrKey, key) || containsText(key, nameOrKey));
  return partial ? partial[1] : null;
}
interface TechLogoImageProps {
  /** Tech name or logoKey, resolved via {@link getTechLogo}. */
  name: string;
  /** Edge length in px for the underlying next/image. */
  size: number;
  /** Extra classes (margins, sizing) applied to the rendered image(s). */
  className?: string;
}

// Renders the logo for a tech. For { dark, light } pairs it renders BOTH
// variants and toggles them purely with CSS (`dark:` classes), so the right
// one shows in each theme with no flash and no client-side theme resolution.
export function TechLogoImage(t0) {
  const $ = _c(28);
  const {
    name,
    size,
    className
  } = t0;
  let logo;
  let t1;
  let t2;
  if ($[0] !== name) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      logo = getTechLogo(name);
      if (!logo) {
        t2 = null;
        break bb0;
      }
      t1 = isThemedLogo(logo);
    }
    $[0] = name;
    $[1] = logo;
    $[2] = t1;
    $[3] = t2;
  } else {
    logo = $[1];
    t1 = $[2];
    t2 = $[3];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  if (t1) {
    const t3 = logo.light;
    let t4;
    if ($[4] !== className) {
      t4 = cn("object-contain dark:hidden", className);
      $[4] = className;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    let t5;
    if ($[6] !== logo.light || $[7] !== name || $[8] !== size || $[9] !== t4) {
      t5 = <Image src={t3} alt={name} width={size} height={size} className={t4} />;
      $[6] = logo.light;
      $[7] = name;
      $[8] = size;
      $[9] = t4;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    const t6 = logo.dark;
    let t7;
    if ($[11] !== className) {
      t7 = cn("hidden object-contain dark:block", className);
      $[11] = className;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    let t8;
    if ($[13] !== logo.dark || $[14] !== name || $[15] !== size || $[16] !== t7) {
      t8 = <Image src={t6} alt={name} width={size} height={size} className={t7} />;
      $[13] = logo.dark;
      $[14] = name;
      $[15] = size;
      $[16] = t7;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    let t9;
    if ($[18] !== t5 || $[19] !== t8) {
      t9 = <>{t5}{t8}</>;
      $[18] = t5;
      $[19] = t8;
      $[20] = t9;
    } else {
      t9 = $[20];
    }
    return t9;
  }
  let t3;
  if ($[21] !== className) {
    t3 = cn("object-contain", className);
    $[21] = className;
    $[22] = t3;
  } else {
    t3 = $[22];
  }
  let t4;
  if ($[23] !== logo || $[24] !== name || $[25] !== size || $[26] !== t3) {
    t4 = <Image src={logo} alt={name} width={size} height={size} className={t3} />;
    $[23] = logo;
    $[24] = name;
    $[25] = size;
    $[26] = t3;
    $[27] = t4;
  } else {
    t4 = $[27];
  }
  return t4;
}